import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: number;
  event_type: 'task_assigned' | 'project_assigned' | 'team_assigned' | 'time_off_request';
  title: string;
  message: string;
  read: boolean;
  read_at?: string;
  created_at: string;
  actor_name?: string;
  target_type?: string;
  target_id?: number;
}

interface ApiResponse {
  notifications: Notification[];
  unread_count: number;
}

export const NotificationBell: React.FC = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
  const token = localStorage.getItem('access_token');

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = (await response.json()) as ApiResponse;
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id: number) => {
    if (!token) return;
    try {
      await fetch(`${API_BASE}/notifications/${id}/mark_as_read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    try {
      await fetch(`${API_BASE}/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!token) return;
    try {
      await fetch(`${API_BASE}/notifications/mark_all_as_read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    setIsOpen(false);

    if (notif.target_type === 'Task' && notif.target_id) {
      navigate(`/manager/tasks/${notif.target_id}`);
    } else if (notif.target_type === 'Project' && notif.target_id) {
      navigate(`/manager/projects/${notif.target_id}`);
    } else if (notif.target_type === 'Team' && notif.target_id) {
      navigate(`/manager/teams/${notif.target_id}`);
    } else if (notif.target_type === 'TimeOffRequest' && notif.target_id) {
      navigate(`/manager/time-off/${notif.target_id}`);
    }
  };

  const getEventEmoji = (eventType: string) => {
    const emojis: { [key: string]: string } = {
      task_assigned: '📋',
      project_assigned: '📁',
      team_assigned: '👥',
      time_off_request: '🏖️',
    };
    return emojis[eventType] || '🔔';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="relative" ref={bellRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-1.5 rounded-lg transition-all duration-200 ${
          isDark ? 'hover:bg-slate-900/50' : 'hover:bg-slate-100'
        }`}
        title="Notifications"
      >
        <svg
          className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 mt-2 w-96 rounded-2xl shadow-2xl z-50 animate-scale-in max-h-[500px] flex flex-col ${
            isDark
              ? 'bg-slate-900 border border-slate-700/50'
              : 'bg-white border border-slate-200 shadow-xl'
          }`}
        >
          <div
            className={`px-5 py-4 border-b flex justify-between items-center flex-shrink-0 ${
              isDark ? 'border-slate-700/50' : 'border-slate-200'
            }`}
          >
            <h3
              className={`text-base font-semibold ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className={`text-xs font-medium transition-colors ${
                  isDark
                    ? 'text-indigo-400 hover:text-indigo-300'
                    : 'text-indigo-600 hover:text-indigo-700'
                }`}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="px-5 py-8 text-center">
                <div className="inline-block w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div
                className={`px-5 py-8 text-center ${
                  isDark ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                <svg
                  className="w-12 h-12 mx-auto mb-2 opacity-50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <p>No notifications yet</p>
              </div>
            )}

            {!loading &&
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  className={`px-5 py-3 border-b transition-colors last:border-b-0 cursor-pointer hover:opacity-80 ${
                    notif.read
                      ? isDark
                        ? 'bg-slate-800/30 border-slate-700/30'
                        : 'bg-slate-50 border-slate-100'
                      : isDark
                        ? 'bg-slate-800/80 border-slate-700/50'
                        : 'bg-indigo-50/50 border-indigo-100/50'
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="text-lg flex-shrink-0 mt-0.5">
                      {getEventEmoji(notif.event_type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium text-sm ${
                          isDark ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {notif.title}
                      </div>
                      <div
                        className={`text-xs mt-1 ${
                          isDark ? 'text-slate-400' : 'text-slate-600'
                        }`}
                      >
                        {notif.message}
                      </div>
                      {notif.actor_name && (
                        <div
                          className={`text-xs mt-1 ${
                            isDark ? 'text-slate-500' : 'text-slate-500'
                          }`}
                        >
                          by {notif.actor_name}
                        </div>
                      )}
                      <div
                        className={`text-xs mt-1 ${
                          isDark ? 'text-slate-500' : 'text-slate-400'
                        }`}
                      >
                        {formatDate(notif.created_at)}
                      </div>
                    </div>

                    <div className="flex gap-1 flex-shrink-0">
                      {!notif.read && (
                        <button
                          onClick={() => handleMarkAsRead(notif.id)}
                          className={`p-1 rounded transition-colors ${
                            isDark
                              ? 'hover:bg-slate-700/50'
                              : 'hover:bg-slate-200'
                          }`}
                          title="Mark as read"
                        >
                          <svg
                            className={`w-4 h-4 ${
                              isDark
                                ? 'text-slate-500'
                                : 'text-slate-400'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notif.id)}
                        className={`p-1 rounded transition-colors ${
                          isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-200'
                        }`}
                        title="Delete"
                      >
                        <svg
                          className={`w-4 h-4 ${
                            isDark
                              ? 'text-slate-500'
                              : 'text-slate-400'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
